/**
 * A Companies House authentication code is exactly 6 alphanumeric characters - letters and digits are both
 * legitimate (real codes mix both, and a code can be all letters). APT080 asked for this to be "numeric only
 * input"; the 3.0 source (Web/Capium.Web/Accounts/GeneralSettings/AuthCodes.aspx, `authcode` validator:
 * `/^[A-Za-z0-9]{6,8}$/`) has always accepted letters too, and `abcdef` is asserted valid server-side
 * (AuthorisationCodeRuleTests.A_six_character_code_or_a_blank_is_valid). Restricting to digits would reject real
 * Companies House codes, so that part of the report is stale, like the filter-dropdown one next to it (BUG-058).
 */
export const AUTH_CODE_LENGTH = 6;
export const AUTH_CODE_PATTERN = /^[A-Za-z0-9]{6}$/;

/** Strips anything but letters/digits, upper-cases, and caps at the code length. */
export function sanitizeAuthCode(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, AUTH_CODE_LENGTH);
}

/** True for a non-empty value that isn't a complete 6-character alphanumeric code. */
export function isAuthCodeInvalid(code: string | null | undefined): boolean {
  const trimmed = (code ?? '').trim();
  return trimmed.length > 0 && !AUTH_CODE_PATTERN.test(trimmed);
}

/** Normalizes a code the same way a save does (trim, upper-case, empty -> null). */
export function normalizeAuthCode(code: string | null | undefined): string | null {
  return (code ?? '').trim().toUpperCase() || null;
}
